import React, { useCallback, useRef } from 'react'
import { ArrowCaret } from '@fs/zion-icon'
import { ListItem } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import {
  getPersonName,
  getRelationshipToPrimaryPersonText,
  useSlsStateContext,
  useSlsHighlightsContext,
  getRelationshipValue,
  getPrimaryRelationshipText,
} from '@fs/zion-sls'
import { useR2StateContext } from '../../../../providers/r2Provider/R2Context'
import {
  getIconByAttachmentStatus,
  getIconBackgroundColor,
  getIconTestId,
} from '../../../../utils/personAttachmentDisplay.utils'
import { usePersonAttachmentData } from '../../../../views/indexing/hooks/usePersonAttachmentData'

/**
 * PersonDisplay
 *
 * Renders a single person as a list item with attachment status icon, name, relationship label,
 * and interaction handlers for selection and highlight hovering.
 *
 * Features:
 * - Shows attachment status via icon (attached, possible match, or no attachment)
 * - Displays person name and relationship to primary person
 * - Highlights person on hover
 * - Handles click to select person
 *
 * @component
 * @param {Object} props
 * @param {Object} props.person - The person object to display
 * @param {Object} [props.record] - Optional record/household object the person belongs to
 * @param {boolean} props.isSelected - Whether this person is currently selected
 * @param {Function} [props.onPersonSelect] - Callback when person is clicked, receives (personId, recordId)
 * @returns {JSX.Element|null} The person list item or null if person is undefined
 */
const PersonDisplay = ({ person, record, isSelected, onPersonSelect = () => {} }) => {
  const [t] = useTranslation()
  const { types } = useSlsStateContext()
  const r2State = useR2StateContext()
  const { hoverHighlights, unHover } = useSlsHighlightsContext()
  const listItemRef = useRef()
  const r2SelectedPersonIdRef = useRef()

  if (isSelected && r2SelectedPersonIdRef.current !== r2State.selectedPersonId && listItemRef.current) {
    r2SelectedPersonIdRef.current = r2State.selectedPersonId
  }

  const { attachment, match } = usePersonAttachmentData(person?.id)

  const personName = getPersonName({ person, justOneName: true })
  const relationshipToPrimary = getRelationshipValue(person, types, record) || ''
  const relationshipOrPrimaryLabel = getRelationshipToPrimaryPersonText(person, types, record, t) || ''
  const relationshipLabel = getPrimaryRelationshipText(relationshipOrPrimaryLabel, relationshipToPrimary)

  // Always show icon based on attachment status
  const IconComponent = getIconByAttachmentStatus(attachment, match)
  const iconBackground = getIconBackgroundColor(attachment, match)
  const iconTestId = getIconTestId(attachment, match)
  const startElement = <ListItem.Icon Icon={IconComponent} background={iconBackground} data-testid={iconTestId} />

  const handleClick = useCallback(() => {
    onPersonSelect(person?.id, record?.id)
  }, [onPersonSelect, person?.id, record?.id])

  const handleMouseEnter = useCallback(() => {
    hoverHighlights({ elementIds: [person?.id] })
  }, [hoverHighlights, person?.id])

  const handleMouseLeave = useCallback(() => {
    unHover()
  }, [unHover])

  const endelementCaretButtonTitle = `${t('person-display.button.person-select', 'Click to view')} ${personName}`

  const endElement = (
    <ListItem.IconButton
      Icon={ArrowCaret}
      color="gray"
      title={endelementCaretButtonTitle}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    />
  )

  if (!person) return null

  return (
    <ListItem
      ref={listItemRef}
      data-testid={personName}
      key={person?.id}
      primaryText={personName}
      secondaryText={relationshipLabel}
      emphasized={!!person?.primary}
      startElement={startElement}
      endElement={endElement}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      selected={isSelected}
    />
  )
}

export default PersonDisplay
